import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import informesBibliotecaAdminService, {
  InformeBibliotecaListItem,
} from '../../services/informesBibliotecaAdminService';
import { INFORMES_MODULOS } from '../../config/informesModulos';
import { getApiErrorMessage } from '../../utils/apiError';

const BibliotecaInformes: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<InformeBibliotecaListItem[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [modulo, setModulo] = useState('Ventas');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await informesBibliotecaAdminService.list();
      setItems(data);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cargar la biblioteca de informes.'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!codigo.trim() || !titulo.trim()) {
      enqueueSnackbar('Código y título son obligatorios.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const created = await informesBibliotecaAdminService.create({
        codigo: codigo.trim(),
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
        modulo: modulo.trim() || 'Ventas',
        planRequerido: 'Free',
      });
      enqueueSnackbar('Informe creado. Subí una versión para publicarlo en el store.', { variant: 'success' });
      setFormOpen(false);
      navigate(`/sistema/biblioteca-informes/${created.id}`);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo crear el informe.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Biblioteca de informes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Catálogo del store: publicá versiones inmutables para que cada empresa las instale.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCodigo('');
                  setTitulo('');
                  setDescripcion('');
                  setModulo('Ventas');
                  setFormOpen(true);
                }}
              >
                Nuevo informe
              </Button>
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No hay informes en la biblioteca.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell>Módulo</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell align="center">Versión publicada</TableCell>
                      <TableCell align="center">Instalaciones</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/sistema/biblioteca-informes/${item.id}`)}
                      >
                        <TableCell>{item.codigo}</TableCell>
                        <TableCell>{item.titulo}</TableCell>
                        <TableCell>{item.modulo}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.planRequerido} color="success" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          {item.versionPublicadaNumero != null ? `v${item.versionPublicadaNumero}` : '—'}
                        </TableCell>
                        <TableCell align="center">{item.instalacionesCount}</TableCell>
                        <TableCell align="center">{item.activo ? 'Activo' : 'Inactivo'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onClose={saving ? undefined : () => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo informe de biblioteca</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              fullWidth
              required
              disabled={saving}
              helperText="Identidad estable. Letras, números, guion y guion bajo."
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              label="Título"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              fullWidth
              required
              disabled={saving}
              inputProps={{ maxLength: 120 }}
            />
            <FormControl fullWidth disabled={saving}>
              <InputLabel id="nuevo-informe-modulo">Módulo</InputLabel>
              <Select
                labelId="nuevo-informe-modulo"
                label="Módulo"
                value={modulo}
                onChange={(e) => setModulo(e.target.value)}
              >
                {INFORMES_MODULOS.map((item) => (
                  <MenuItem key={item.modulo} value={item.modulo}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value.slice(0, 200))}
              fullWidth
              multiline
              minRows={2}
              disabled={saving}
              helperText={`${descripcion.length}/200`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCreate()} disabled={saving}>
            {saving ? 'Guardando…' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BibliotecaInformes;
